import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API_ENDPOINT } from '../../constants/constants';
import { getHeader, getToken } from '../../utils/util';
import { toast } from 'react-toastify';
import './sendBroadcast.css';

// Send broadcast — built to the "Send Broadcast" handoff (Send Broadcast.dc.html).
// It is its own dialog (header, body, footer), so Broadcast.js renders it
// directly rather than inside MyModal's titled frame.
//
// Audiences map to the API's userGroup values. "All candidates" can only mean
// people who took a mock test: assessment candidates give a name, no email.

const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;
const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-IN') : '…');

const Icon = ({ d, size = 16, sw = 2.1 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {d.map((p, i) => <path key={i} d={p} />)}
    </svg>
);
const Txt = ({ children, style }) => <span style={{ font: "700 14px/1 'DM Sans'", ...style }}>{children}</span>;

const SendBroadcast = ({ show, onClose, onSent }) => {
    const [group, setGroup] = useState('trainers');
    const [counts, setCounts] = useState({});
    const [emails, setEmails] = useState([]);
    const [draft, setDraft] = useState('');
    const [subject, setSubject] = useState('');
    const [directory, setDirectory] = useState(null);
    const [suggestOpen, setSuggestOpen] = useState(false);
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const editorRef = useRef(null);
    const imageInput = useRef(null);

    useEffect(() => {
        if (!show) return;
        setGroup('trainers');
        setEmails([]);
        setDraft('');
        setSubject('');
        if (editorRef.current) editorRef.current.innerHTML = '';
        axios.get(API_ENDPOINT + 'admin/broadcast-audiences', getHeader())
            .then(res => { if (res.data.success) setCounts(res.data.message) })
            .catch(() => {});
    }, [show]);

    // Trainer suggestions for the email field, fetched once on first use.
    useEffect(() => {
        if (!show || group !== 'specific' || directory) return;
        axios.get(API_ENDPOINT + 'admin/trainer-directory', getHeader())
            .then(res => { if (res.data.success) setDirectory(res.data.message.filter(t => t.email)) })
            .catch(() => {});
    }, [show, group, directory]);

    useEffect(() => {
        if (!show) return;
        const onKey = (e) => { if (e.key === 'Escape' && !sending) onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [show, sending, onClose]);

    if (!show) return null;

    // ---- recipients ----
    const addEmails = (raw) => {
        const list = String(raw).split(/[,\s;]+/).map(s => s.trim()).filter(Boolean);
        if (!list.length) { setDraft(''); return; }
        const bad = list.filter(e => !EMAIL_RE.test(e));
        const good = list.filter(e => EMAIL_RE.test(e));
        setEmails(prev => {
            const have = new Set(prev.map(e => e.toLowerCase()));
            return [...prev, ...good.filter(e => !have.has(e.toLowerCase()))];
        });
        // Keep what can't be added in the box so it can be corrected.
        setDraft(bad.join(', '));
        if (bad.length) toast.error(`Not a valid email: ${bad.join(', ')}`);
    };
    const onDraftChange = (e) => {
        const v = e.target.value;
        setSuggestOpen(true);
        if (/[,;]$/.test(v)) addEmails(v); else setDraft(v);
    };
    const onEmailKey = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); addEmails(draft); }
        else if (e.key === 'Backspace' && !draft && emails.length) setEmails(emails.slice(0, -1));
    };
    const q = draft.trim().toLowerCase();
    const suggestions = q && directory
        ? directory
            .filter(t => !emails.some(e => e.toLowerCase() === t.email.toLowerCase()))
            .filter(t => `${t.name} ${t.email} ${t.phone}`.toLowerCase().includes(q))
            .slice(0, 6)
        : [];

    const groups = [
        { key: 'users', label: 'All users', count: fmt(counts.users) },
        { key: 'trainers', label: 'All trainers', count: fmt(counts.trainers) },
        { key: 'candidates', label: 'All candidates', count: fmt(counts.candidates), title: 'Everyone who took a mock test with an email. Assessment candidates give no email.' },
        { key: 'specific', label: 'Specific trainers', count: String(emails.length) },
    ];
    const cur = groups.find(g => g.key === group);
    const n = emails.length;
    const recipientLabel = group === 'specific'
        ? `${n} trainer${n === 1 ? '' : 's'}`
        : `${cur.count} ${cur.label.replace('All ', '')}`;

    // ---- editor (contentEditable, as in the handoff) ----
    const run = (cmd, val) => (e) => {
        e.preventDefault();
        if (editorRef.current) editorRef.current.focus();
        document.execCommand(cmd, false, val);
    };
    const addLink = (e) => {
        e.preventDefault();
        const u = window.prompt('Link URL');
        if (u && /^(https?:|mailto:)/i.test(u.trim())) run('createLink', u.trim())(e);
        else if (u) toast.error('Links must start with https://, http:// or mailto:');
    };
    const pickImage = (e) => { e.preventDefault(); if (!uploading && imageInput.current) imageInput.current.click(); };
    const onImage = (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        if (!/^image\//.test(file.type)) return toast.error('Please choose an image file');
        // Remember where the caret was; the upload takes a moment.
        const sel = window.getSelection();
        const range = sel && sel.rangeCount && editorRef.current && editorRef.current.contains(sel.anchorNode)
            ? sel.getRangeAt(0).cloneRange() : null;
        const fd = new FormData();
        fd.append('image', file);
        fd.append('desc', 'Broadcast image');
        setUploading(true);
        axios.post(API_ENDPOINT + 'sam/upload-image', fd, { headers: { token: getToken() } })
            .then(res => {
                const d = res.data;
                const url = d && d.success && d.message && d.message.data && d.message.data.image;
                if (!url) return toast.error((d && typeof d.message === 'string' && d.message) || 'Image upload failed');
                editorRef.current.focus();
                if (range) { sel.removeAllRanges(); sel.addRange(range); }
                document.execCommand('insertImage', false, url);
            })
            .catch(() => toast.error('Image upload failed'))
            .finally(() => setUploading(false));
    };
    // Pasted text arrives plain; Word and web pages bring styles that render
    // unpredictably across email clients.
    const onPaste = (e) => {
        e.preventDefault();
        document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
    };

    const tools = [
        { title: 'Bold', icon: <Txt>B</Txt>, run: run('bold') },
        { title: 'Italic', icon: <Txt style={{ fontStyle: 'italic', fontFamily: 'Georgia,serif' }}>I</Txt>, run: run('italic') },
        { title: 'Underline', icon: <Txt style={{ textDecoration: 'underline' }}>U</Txt>, run: run('underline') },
        { title: 'Strikethrough', icon: <Txt style={{ textDecoration: 'line-through' }}>S</Txt>, run: run('strikeThrough') },
        { sep: true },
        { title: 'Heading', icon: <Txt style={{ fontSize: '12.5px' }}>H1</Txt>, run: run('formatBlock', 'h3') },
        { title: 'Paragraph', icon: <Txt style={{ fontSize: '15px' }}>¶</Txt>, run: run('formatBlock', 'p') },
        { sep: true },
        { title: 'Bulleted list', icon: <Icon d={['M9 6h11M9 12h11M9 18h11', 'M4 6h.01M4 12h.01M4 18h.01']} />, run: run('insertUnorderedList') },
        { title: 'Numbered list', icon: <Icon d={['M10 6h10M10 12h10M10 18h10', 'M4 4h1v4M4 8h2M4 14h2l-2 3h2']} />, run: run('insertOrderedList') },
        { title: 'Link', icon: <Icon d={['M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1', 'M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1']} />, run: addLink },
        { title: uploading ? 'Uploading…' : 'Image', icon: uploading ? <span className="sb-spin" /> : <Icon d={['M4 5h16v14H4z', 'M4 16l5-5 4 4 3-3 4 4', 'M15 9h.01']} />, run: pickImage },
        { sep: true },
        { title: 'Clear formatting', icon: <Icon d={['M4 7V5h12v2M10 5l-3 14M5 19h6', 'M16 15l5 5M21 15l-5 5']} />, run: run('removeFormat') },
        { title: 'Undo', icon: <Icon d={['M9 14 4 9l5-5', 'M4 9h11a5 5 0 0 1 0 10h-3']} />, run: run('undo') },
        { title: 'Redo', icon: <Icon d={['M15 14l5-5-5-5', 'M20 9H9a5 5 0 0 0 0 10h3']} />, run: run('redo') },
    ];

    // ---- send ----
    const onSend = () => {
        const html = editorRef.current ? editorRef.current.innerHTML.trim() : '';
        const hasBody = editorRef.current && (editorRef.current.textContent.trim() || editorRef.current.querySelector('img'));
        const pending = draft.trim();
        const all = pending && EMAIL_RE.test(pending) && !emails.includes(pending) ? [...emails, pending] : emails;
        if (group === 'specific' && !all.length) return toast.error('Add at least one email address');
        if (!subject.trim()) return toast.error('Please enter a subject');
        if (!hasBody) return toast.error('Please write a message');
        setSending(true);
        axios.post(API_ENDPOINT + 'admin/broadcast-msg', {
            title: subject.trim(),
            message: html,
            userGroup: group === 'specific' ? 'emails' : group,
            emails: group === 'specific' ? all.join(', ') : '',
        }, getHeader())
            .then(res => {
                const d = res.data;
                if (d.success) {
                    toast.success(d.message || 'Broadcast sent');
                    onSent();
                } else {
                    toast.error(d.message || 'Broadcast failed');
                }
            })
            .catch(err => toast.error(`Broadcast failed: ${err.message}`))
            .finally(() => setSending(false));
    };

    return (
        <div className="sb-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !sending) onClose(); }}>
            <div className="sb-card" role="dialog" aria-modal="true" aria-labelledby="sb-title">
                <div className="sb-head">
                    <div className="sb-head-icon">
                        <Icon size={20} sw={2} d={['M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1Z', 'M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13']} />
                    </div>
                    <div className="sb-head-text">
                        <h2 id="sb-title">Send broadcast</h2>
                        <div>Email an announcement to your users</div>
                    </div>
                    <button type="button" className="sb-close" onClick={onClose} disabled={sending} aria-label="Close">
                        <Icon size={16} sw={2.2} d={['M18 6 6 18M6 6l12 12']} />
                    </button>
                </div>

                <div className="sb-body">
                    <div>
                        <label className="sb-label">Send to</label>
                        <div className="sb-groups">
                            {groups.map(g => (
                                <button key={g.key} type="button" title={g.title}
                                    className={`sb-group${g.key === group ? ' on' : ''}`}
                                    onClick={() => setGroup(g.key)}>
                                    {g.label}<span className="sb-count">{g.count}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {group === 'specific' && (
                        <div>
                            <label className="sb-label" htmlFor="sb-emails">Trainer emails <span>· press Enter or comma to add</span></label>
                            <div className="sb-chips" onClick={() => document.getElementById('sb-emails').focus()}>
                                {emails.map(e => (
                                    <span key={e} className="sb-chip">{e}
                                        <button type="button" aria-label={`Remove ${e}`} onClick={() => setEmails(emails.filter(x => x !== e))}>
                                            <Icon size={9} sw={3.5} d={['M18 6 6 18M6 6l12 12']} />
                                        </button>
                                    </span>
                                ))}
                                <input id="sb-emails" value={draft} onChange={onDraftChange} onKeyDown={onEmailKey}
                                    onFocus={() => setSuggestOpen(true)}
                                    onBlur={() => { setSuggestOpen(false); if (draft.trim() && EMAIL_RE.test(draft.trim())) addEmails(draft); }}
                                    placeholder={emails.length ? '' : 'name@company.com'} autoComplete="off" />
                                {suggestOpen && suggestions.length > 0 && (
                                    <div className="sb-suggest">
                                        {suggestions.map(t => (
                                            <button key={t.phone} type="button"
                                                onMouseDown={(e) => { e.preventDefault(); setEmails([...emails, t.email]); setDraft(''); }}>
                                                <span className="sb-suggest-name">{t.name || t.email}</span>
                                                <span className="sb-suggest-meta">{t.email}{t.status ? ` · ${t.status}` : ''}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="sb-label" htmlFor="sb-subject">Subject</label>
                        <input id="sb-subject" className="sb-input" value={subject} onChange={e => setSubject(e.target.value)} placeholder="e.g. New assessment available" />
                    </div>

                    <div>
                        <label className="sb-label">Message</label>
                        <div className="sb-editor">
                            <div className="sb-toolbar">
                                {tools.map((t, i) => t.sep
                                    ? <span key={i} className="sb-sep" />
                                    : <button key={i} type="button" title={t.title} aria-label={t.title} onMouseDown={t.run}>{t.icon}</button>)}
                            </div>
                            <div ref={editorRef} className="sb-content" contentEditable suppressContentEditableWarning
                                onPaste={onPaste} role="textbox" aria-multiline="true" aria-label="Message" />
                        </div>
                        <input ref={imageInput} type="file" accept="image/*" hidden onChange={onImage} />
                    </div>
                </div>

                <div className="sb-foot">
                    <div className="sb-foot-to">Sending to <span>{recipientLabel}</span></div>
                    <button type="button" className="sb-cancel" onClick={onClose} disabled={sending}>Cancel</button>
                    <button type="button" className="sb-send" onClick={onSend} disabled={sending}>
                        {sending ? <span className="sb-spin light" /> : <Icon size={16} sw={2.2} d={['M22 2 11 13M22 2l-7 20-4-9-9-4Z']} />}
                        {sending ? 'Sending…' : 'Send broadcast'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SendBroadcast;
